import { useTranslation } from "@next-platform/client-common-core";
import { Spinner } from "@next-platform/client-common-spicy-ui";
import type { LorebookEntry } from "@next-platform/packages-ts-types";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import EntriesListEmptyState from "./EntriesListEmptyState";
import EntriesListHeader from "./EntriesListHeader";
import EntriesListLoadMoreError from "./EntriesListLoadMoreError";
import EntriesListSkeleton from "./EntriesListSkeleton";
import EntryCard from "./EntryCard";
import type { EntriesListServerProps } from "./types";

const SENTINEL_ROOT_MARGIN = "100px";

function EntriesListServer({
  entries,
  totalEntryCount,
  searchValue,
  onSearchChange,
  onEditEntry,
  onEndReached,
  isLoadingFirstPage,
  isLoadingMore,
  hasMore,
  hasError,
  onRetry,
  onRetryLoadMore,
  listHeader,
  panelTitle,
}: EntriesListServerProps) {
  const { t } = useTranslation();
  const refSentinel = useRef<HTMLDivElement>(null);
  const isSearchActive = Boolean(searchValue.trim());
  const isLoadMoreErrorVisible = hasError && entries.length > 0 && !isLoadingMore;
  const shouldEnablePagination = hasMore && !isSearchActive && !isLoadMoreErrorVisible;

  useEffect(() => {
    // Skip while a fetch is in flight; reconnecting after entries.length changes re-triggers the
    // observer's initial intersection check so load-more continues when the sentinel stays in view.
    if (!shouldEnablePagination || isLoadingMore || isLoadingFirstPage) return undefined;

    const sentinelElement = refSentinel.current;
    if (!sentinelElement) return undefined;

    const observer = new IntersectionObserver(
      (observerEntries) => {
        if (observerEntries[0]?.isIntersecting) {
          onEndReached();
        }
      },
      { rootMargin: SENTINEL_ROOT_MARGIN },
    );

    observer.observe(sentinelElement);
    return () => observer.disconnect();
  }, [shouldEnablePagination, isLoadingMore, isLoadingFirstPage, entries.length, onEndReached]);

  const emptyText = useMemo(() => {
    if (hasError) {
      return t("lorebook:entries.loadError");
    }
    if (isSearchActive) {
      return t("lorebook:entries.noResultsForSearch", {
        query: searchValue.trim(),
      });
    }
    return t("lorebook:entries.emptyState");
  }, [hasError, isSearchActive, searchValue, t]);

  const loadMoreErrorText = t("lorebook:entries.loadMoreError");

  const shouldShowEmptyState = entries.length === 0 && !isLoadingFirstPage;

  const handleEditEntry = useCallback(
    (entry: LorebookEntry) => {
      onEditEntry?.(entry);
    },
    [onEditEntry],
  );

  const entryCards = useMemo(
    () =>
      entries.map((entry) => <EntryCard key={entry.id ?? entry.name} entry={entry} onPress={handleEditEntry} data-testid="EntriesList-EntryCard" />),
    [entries, handleEditEntry],
  );

  return (
    <div className="flex flex-col gap-4" data-testid="EntriesListServer">
      <EntriesListHeader
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        totalEntryCount={totalEntryCount}
        visibleEntryCount={entries.length}
        listHeader={listHeader}
        panelTitle={panelTitle}
      />

      {isLoadingFirstPage && entries.length === 0 ? <EntriesListSkeleton /> : null}

      {entries.length > 0 ? <div className="flex flex-col">{entryCards}</div> : null}

      <div data-testid="EntriesListServer-Footer">
        {isLoadingMore ? (
          <div className="flex justify-center py-4" data-testid="EntriesListServer-FooterLoader">
            <Spinner size="large" />
          </div>
        ) : null}
        {shouldShowEmptyState ? <EntriesListEmptyState message={emptyText} hasError={hasError} onRetry={onRetry} /> : null}
        {isLoadMoreErrorVisible ? <EntriesListLoadMoreError message={loadMoreErrorText} onRetry={onRetryLoadMore} /> : null}
      </div>

      {shouldEnablePagination ? <div ref={refSentinel} className="h-px w-full" aria-hidden="true" data-testid="EntriesListServer-Sentinel" /> : null}
    </div>
  );
}

export default React.memo(EntriesListServer);
